import { ReloadOutlined, SaveOutlined, SettingOutlined } from "@ant-design/icons";
import { Alert, Button, Card, Form, Input, InputNumber, Select, Spin, Tag, Typography, message } from "antd";
import { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { connect } from "react-redux";
import { isEqual } from "underscore";
import api from "../../../api/api";
import CheckPermission from "../../../Util/CheckPermission";
import { getInitialValues, getSettingValue, isObject, settingDefinitions } from "./settingDefinitions";
import styles from "./SiteSettings.module.css";

const SettingCard = ({ settingKey, record, canEdit, canCreate, onSaved }) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const definition = (!record || record.valueJson == null || isObject(record.valueJson))
    ? settingDefinitions[settingKey] : undefined;
  const editable = record ? canEdit : canCreate;
  const initialValues = getInitialValues(definition, record?.valueJson);

  const hasChanges = (values) => {
    try {
      return !isEqual(
        getSettingValue(definition, record?.valueJson, values),
        getSettingValue(definition, record?.valueJson, initialValues)
      );
    } catch {
      // Keep invalid JSON editable so form validation can explain the error.
      return true;
    }
  };

  useLayoutEffect(() => {
    form.resetFields();
    setDirty(false);
  }, [record, form]);

  const save = async (values) => {
    if (!editable || saving || !hasChanges(values)) return;
    setSaving(true);
    try {
      const valueJson = getSettingValue(definition, record?.valueJson, values);
      const response = record
        ? await api.put(`/settings/${record.id}`, { data: { valueJson } })
        : await api.post("/settings", { data: { key: settingKey, valueJson } });
      const saved = response.data?.data;
      if (!saved?.id || !saved.attributes) throw new Error("Invalid settings response");
      onSaved({ id: saved.id, ...saved.attributes });
      message.success("تنظیمات با موفقیت ذخیره شد.");
    } catch (error) {
      message.error(error.response?.data?.error?.message || "ذخیره تنظیمات انجام نشد. دوباره تلاش کنید.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card role="region" aria-label={definition?.title || settingKey}
      title={definition?.title || settingKey} extra={!record && <Tag>مقادیر پیش‌فرض</Tag>}>
      {definition && <Typography.Paragraph type="secondary">{definition.description}</Typography.Paragraph>}
      {!record && <Typography.Paragraph type="secondary">این تنظیم هنوز ثبت نشده است. با ذخیره، تنظیم جدید ثبت می‌شود.</Typography.Paragraph>}
      <Form form={form} name={`setting-${settingKey}`} layout="vertical" initialValues={initialValues}
        disabled={!editable || saving} onValuesChange={(_, values) => setDirty(hasChanges(values))} onFinish={save}>
        {definition ? (
          <div className={styles.fields}>
            {definition.fields.map((field) => (
              <Form.Item key={field.key} name={field.key} label={field.label}
                extra={field.defaultValue !== undefined && !field.options ? `مقدار پیش‌فرض: ${field.defaultValue}` : undefined}
                rules={field.options ? [{ required: true, message: "درگاه پرداخت را انتخاب کنید." }] : [{
                  validator: (_, value) => value == null || (Number.isSafeInteger(value) && value >= field.min)
                    ? Promise.resolve()
                    : Promise.reject(new Error(`عدد صحیح بزرگ‌تر یا مساوی ${field.min} وارد کنید.`)),
                }]}>
                {field.options ? <Select options={field.options} /> : <InputNumber min={field.min} max={Number.MAX_SAFE_INTEGER} className={styles.number} />}
              </Form.Item>
            ))}
          </div>
        ) : (
          <Form.Item name="json" label="مقدار تنظیم (JSON)" rules={[{
            validator: (_, value) => {
              try { JSON.parse(value); return Promise.resolve(); }
              catch { return Promise.reject(new Error("ساختار JSON معتبر نیست.")); }
            },
          }]}>
            <Input.TextArea rows={10} className={styles.json} spellCheck={false} />
          </Form.Item>
        )}
        {editable ? (
          <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={saving} disabled={saving || !dirty}>ذخیره تنظیمات</Button>
        ) : <Typography.Text type="secondary">شما دسترسی {record ? "ویرایش" : "ثبت"} این تنظیم را ندارید.</Typography.Text>}
      </Form>
    </Card>
  );
};

const SiteSettings = ({ canView, canEdit, canCreate }) => {
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const records = [];
      let page = 1;
      let pageCount = 1;
      do {
        const response = await api.get("/settings", { params: {
          "pagination[page]": page, "pagination[pageSize]": 100, sort: "key:asc",
        } });
        if (!Array.isArray(response.data?.data)) throw new Error("Invalid settings response");
        records.push(...response.data.data.map((item) => ({ id: item.id, ...item.attributes })));
        pageCount = response.data.meta?.pagination?.pageCount || 1;
        page++;
      } while (page <= pageCount);
      setSettings(records);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (canView) load(); }, [canView, load]);

  if (!canView) return <Alert type="warning" showIcon message="شما دسترسی به این بخش را ندارید." />;
  const keys = Array.from(new Set([...Object.keys(settingDefinitions), ...settings.map((item) => item.key)]))
    .filter((key) => key !== "formTest");

  return (
    <div className={styles.page} dir="rtl">
      <div className={styles.header}>
        <div>
          <Typography.Title level={3}><SettingOutlined /> تنظیمات سایت</Typography.Title>
          <Typography.Text type="secondary">تنظیمات هر بخش را جداگانه ویرایش و ذخیره کنید.</Typography.Text>
        </div>
      </div>
      {loading ? <div className={styles.loading}><Spin tip="در حال دریافت تنظیمات..." /></div> : error ? (
        <Alert type="error" showIcon message="دریافت تنظیمات انجام نشد."
          action={<Button onClick={load} icon={<ReloadOutlined />}>تلاش مجدد</Button>} />
      ) : keys.map((key) => (
        <SettingCard key={key} settingKey={key} record={settings.find((item) => item.key === key)}
          canEdit={canEdit} canCreate={canCreate}
          onSaved={(saved) => setSettings((previous) => [...previous.filter((item) => item.key !== saved.key), saved])} />
      ))}
    </div>
  );
};

export default connect((state) => ({
  canView: CheckPermission("setting_list", state.userData?.permissions),
  canEdit: CheckPermission("setting_edit", state.userData?.permissions),
  canCreate: CheckPermission("setting_create", state.userData?.permissions),
}))(SiteSettings);
